/**
 * 「誰か」の持ち方。
 *
 * **アカウントではない。** 登録もメールもパスワードも作らない。
 * サーバーが発行した内部 ID を端末に置き、本人には 8 文字の
 * **引き継ぎコード**だけを見せる。別端末でコードを入れれば記録が繋がる。
 *
 * なぜ要るか:
 * **iOS Safari は 7 日で消す。** ITP は 7 日間サイトへの操作が無いと
 * localStorage を削除する。端末生成の ID しか持たないと、
 * 1 週間空けた人は自己ベストも順位も、いずれ買ったものも失う。
 * インストール済み PWA は対象外なので、ホーム画面に置いてもらうことと
 * この仕組みの両方で守る。
 *
 * **繋がらなくても遊べることは崩さない。** サーバーが無い／落ちているときは
 * 端末内の ID にそのまま落ちる。ゲーム側はこの違いを知らない。
 */

import { Records, type RemoteRun } from '../records';

const ID_KEY = 'etw:player';
const CODE_KEY = 'etw:player-code';

export interface Identity {
  readonly playerId: string;
  /** サーバー発行のときだけ入る。端末内 ID には引き継ぎコードが無い。 */
  readonly code: string | null;
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // 保存できなくても遊べる方を優先する。
  }
}

/** API の場所。設定されていなければサーバー機能はすべて無効。 */
export function apiBase(): string | null {
  const base = import.meta.env?.VITE_API_BASE as string | undefined;
  return base ? base.replace(/\/$/, '') : null;
}

/** 端末生成の匿名 ID。サーバーが無いときはこれだけで動く。 */
function localId(): string {
  const existing = read(ID_KEY);
  if (existing) return existing;
  const id = `p_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  write(ID_KEY, id);
  return id;
}

/** いま持っている身元。通信しない。ゲーム中はこれを使う。 */
export function currentIdentity(): Identity {
  return { playerId: localId(), code: read(CODE_KEY) };
}

/**
 * サーバー発行の ID をまだ持っていなければ、1 回だけ発行してもらう。
 *
 * 失敗しても投げない。**コードが無いことはゲームを止める理由にならない。**
 */
export async function ensureIdentity(): Promise<Identity> {
  const base = apiBase();
  const current = currentIdentity();
  if (!base || current.code) return current;

  try {
    const res = await fetch(`${base}/player`, { method: 'POST' });
    if (!res.ok) return current;
    const body = (await res.json()) as { playerId?: string; code?: string };
    if (!body.playerId || !body.code) return current;
    write(ID_KEY, body.playerId);
    write(CODE_KEY, body.code);
    return { playerId: body.playerId, code: body.code };
  } catch {
    return current;
  }
}

export type RestoreResult =
  | { ok: true; identity: Identity; restored: number; staleServer: boolean }
  | { ok: false; reason: 'offline' | 'not-found' | 'bad-code' | 'no-server' };

/**
 * 引き継ぎコードで、別端末の身元と**記録**を引き継ぐ。
 *
 * **身元だけ移しても意味がない。** 記録は端末の中にあるので、
 * 新しい端末では何も表示されず、「引き継げます」が嘘になる。
 * サーバーが持っている自己ベストを一緒に取り戻して、手元の表へ併合する。
 *
 * 併合であって上書きではないので、この端末で走ったぶんは消えない。
 */
export async function restoreIdentity(rawCode: string): Promise<RestoreResult> {
  const base = apiBase();
  if (!base) return { ok: false, reason: 'no-server' };

  const code = rawCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (code.length !== 8) return { ok: false, reason: 'bad-code' };

  try {
    const res = await fetch(`${base}/player/restore`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (res.status === 404) return { ok: false, reason: 'not-found' };
    if (!res.ok) return { ok: false, reason: 'bad-code' };
    const body = (await res.json()) as { playerId?: string; records?: RemoteRun[] };
    if (!body.playerId) return { ok: false, reason: 'not-found' };
    write(ID_KEY, body.playerId);
    write(CODE_KEY, code);

    // **「記録が無い」と「サーバーが古い」を混ぜない。**
    // 古い Worker は records を返さないので、`[]` ではなく未定義になる。
    // 同じ「0 件」でも、直し方がまったく違う。
    const staleServer = body.records === undefined;

    // 記録の取り込みで転んでも、身元の引き継ぎ自体は成立させる。
    let restored = 0;
    try {
      restored = new Records().importRemote(body.records ?? []);
    } catch {
      restored = 0;
    }
    return { ok: true, identity: { playerId: body.playerId, code }, restored, staleServer };
  } catch {
    return { ok: false, reason: 'offline' };
  }
}

/** 読ませるための区切り。4 文字ずつに割る。 */
export function formatCode(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4)}`;
}

/**
 * ホーム画面から起動しているか。
 *
 * インストール済みなら Safari の 7 日削除の対象外なので、
 * 「ホーム画面に追加」の案内を出す必要が無い。
 */
export function isInstalled(): boolean {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}
